"use client";

import * as React from "react";
import { motion, useMotionValue, useTransform, useSpring, type MotionValue } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { GlassSurface } from "../primitives/GlassSurface";
import { NX_RING_CLIP } from "../lib/onyx-material";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useNavRailMagnify } from "../primitives/NavRailMagnifyContext";

/**
 * RailNavButton — a navigation control for the app's left rail, rendered as a
 * LARGE GLASS ICON TILE with its name beside it.
 *
 * The hero is the frosted tile: the icon sits inside a two-layer `GlassSurface`
 * (the `nx-m` notch, `--glass*` tokens), so the control reads as "a large glass
 * icon first." The label is VISIBLE AT REST to the right of the tile (issue
 * #165 — the resting state shows the name). In a dock context (a shared cursor
 * `mousePos` is passed) the tile magnifies via proximity to the cursor, driven
 * by a transform SCALE spring (the macOS-dock feel) — never by animating the
 * box width/height (that reflows the row and shoves the label every frame).
 *
 * Three label modes (`labelMode`):
 *   - `"rest"`  (default) — the name is always visible beside the tile.
 *   - `"hover"` — the name is collapsed at rest and slides in on hover / focus
 *     (the legacy #144 behaviour; `revealOnHover` maps onto this).
 *   - `"tooltip"` — no inline label; the tile stands alone (a magnify-dock
 *     context that uses a separate tooltip). The `<span>{label}</span>` still
 *     renders for the a11y tree, just visually hidden.
 *
 * Dock composition (future `NavRail`, #156): pass a shared cursor `mousePos`
 * `MotionValue` + `axis` and the tile drives its scale off the SAME
 * proximity-magnify ramp `MorphingRail` uses, so a rail of these reads as one
 * dock. Standalone (no `mousePos`) the tile is a static glass tile — there is
 * no hover/focus enlarge pop, only the proximity magnify in a dock.
 *
 * - `icon` is required; `label` is the accessible name (also set via
 *   `aria-label`, so the control is named in every label mode).
 * - reduced-motion safe: under `prefers-reduced-motion` the magnify spring is
 *   skipped (colour-only state changes); the glass tile + name still render.
 * - brand tokens only — all colour/shadow comes from `GlassSurface`'s glass
 *   tokens + the cva `--primary` tint; zero raw hex here.
 *
 * @example
 *   <RailNavButton icon={<Home />} label="Home" variant="active" onClick={go} />
 *   <RailNavButton icon={<Compass />} label="Explore" size="compact" />
 */

/* ----------------------------------------------------------------------------
 * Magnify ramp — proximity-magnify recipe shared with MorphingRail's dock.
 * Kept local so RailNavButton is self-contained (the dock-magnify shared
 * module + MorphingRail refactor is a separate, out-of-lane change). Values
 * are the canonical React Bits Pro `navigation-4` numbers, identical to
 * MorphingRail's so the two surfaces feel the same in a future NavRail.
 * ------------------------------------------------------------------------- */
const MAGNIFY_DISTANCE = [-150, -75, 0, 75, 150] as const;
const MAGNIFY_SPRING = { mass: 0.1, stiffness: 150, damping: 12 } as const;
const MAGNIFY_MULTIPLIERS = [1, 1.05, 1.2, 1.05, 1] as const;

/** Tile edge length in px, by size. "Large glass icon" — bigger than the bare
 * 20px glyph the old row showed. Mirrors MorphingRail's tile scale (default
 * 48 ≈ its `lg`, compact 40 ≈ its `default`) so the dock surfaces match. */
const TILE_PX = { default: 48, compact: 40 } as const;
/** Lucide glyph size inside the tile, by size. */
const ICON_PX = { default: 22, compact: 20 } as const;

export const railNavButtonVariants = cva(
  [
    "group relative flex w-full cursor-pointer items-center rounded-lg text-left font-medium",
    "transition-colors duration-320 ease-smooth-out",
    "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        active: "bg-primary/10 text-primary",
        inactive: "text-muted-foreground hover:bg-muted hover:text-foreground",
      },
      size: {
        // Comfortable default; `compact` matches the rail's <=900px breakpoint.
        default: "gap-3 px-2 py-1.5 text-sm",
        compact: "gap-2.5 px-2 py-1 text-[13px]",
      },
    },
    defaultVariants: {
      variant: "inactive",
      size: "default",
    },
  },
);

export type RailNavLabelMode = "rest" | "hover" | "tooltip";

export interface RailNavButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof railNavButtonVariants> {
  /** Leading icon, shown inside the glass tile. Required. */
  icon: React.ReactNode;
  /** Row label + accessible name. */
  label: string;
  /**
   * How the label is shown:
   *   - `"rest"` (default) — visible at rest beside the tile (issue #165).
   *   - `"hover"` — collapsed at rest, revealed on hover / focus.
   *   - `"tooltip"` — no inline label (the tile alone; label kept in the a11y
   *     tree, visually hidden — pair with an external tooltip).
   */
  labelMode?: RailNavLabelMode;
  /**
   * @deprecated Use `labelMode`. Back-compat alias for the #144 API:
   *   `true`  → `labelMode="hover"`  (collapse at rest, reveal on hover)
   *   `false` → `labelMode="rest"`   (label always visible)
   * `labelMode` wins when both are passed. When neither is passed the default
   * is `"rest"` (name shown at rest), per issue #165.
   */
  revealOnHover?: boolean;
  /**
   * Optional shared cursor position (`clientX`/`clientY`) for dock-context
   * proximity magnify. When passed (e.g. by a future `NavRail`), the tile sizes
   * off the SAME ramp `MorphingRail` uses so a rail of these reads as one dock.
   * When omitted, the tile uses a self-contained hover/focus scale spring.
   */
  mousePos?: MotionValue<number>;
  /**
   * Axis the `mousePos` motion value tracks. `"vertical"` (default) reads
   * `clientY` distance; `"horizontal"` reads `clientX`. Only consulted when
   * `mousePos` is passed.
   */
  axis?: "vertical" | "horizontal";
  /**
   * Disable all motion (the enlarge spring / magnify). Default `true`. Reduced-
   * motion users get this automatically; pass `false` to force the static tile.
   */
  motion?: boolean;
}

export const RailNavButton = React.forwardRef<HTMLButtonElement, RailNavButtonProps>(
  (
    {
      icon,
      label,
      variant,
      size,
      labelMode,
      revealOnHover,
      mousePos,
      axis = "vertical",
      motion: motionProp = true,
      className,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const useMotion = motionProp && reduced !== true;
    // Inside a <NavRail>, the rail owns the single shared cursor MotionValue and
    // publishes it through NavRailMagnifyContext (the one-listener rule). When no
    // explicit `mousePos` prop is passed, fall back to that shared value so the
    // dock proximity-magnify works in a rail; outside a rail the context default
    // is null, so the tile stays static.
    const rail = useNavRailMagnify();
    const sizeKey = size ?? "default";
    const tilePx = TILE_PX[sizeKey];
    const iconPx = ICON_PX[sizeKey];

    // Resolve the label mode: explicit `labelMode` wins; else map the legacy
    // `revealOnHover` (true→hover, false→rest); else default to "rest" (#165).
    const mode: RailNavLabelMode = labelMode ?? (revealOnHover === true ? "hover" : "rest");

    // --- Proximity magnify (dock context). Hooks run unconditionally (rules of
    // hooks); whether the spring reaches `style` is gated below on context +
    // motion. When no `mousePos` is supplied we feed a constant Infinity so the
    // transform clamps to the base multiplier 1 (a static tile, no hover pop):
    // there is intentionally NO standalone hover/focus enlarge — only the dock
    // proximity magnify.
    const tileRef = React.useRef<HTMLDivElement>(null);
    const fallbackPos = useMotionValue<number>(Infinity);
    const pos = mousePos ?? rail.mouseAxisPos ?? fallbackPos;
    const distance = useTransform(pos, (val: number) => {
      const bounds = tileRef.current?.getBoundingClientRect();
      if (!bounds) return Infinity;
      return axis === "vertical"
        ? val - bounds.y - bounds.height / 2
        : val - bounds.x - bounds.width / 2;
    });
    // Map distance straight to the MULTIPLIER curve (NOT px*multiplier): the box
    // stays FIXED at `tilePx` and we drive a transform SCALE. A scale is GPU-
    // composited and triggers no layout, so the tile never reflows and the label
    // never shifts (the old width/height spring shoved the row every frame).
    const scale = useSpring(
      useTransform(distance, [...MAGNIFY_DISTANCE], [...MAGNIFY_MULTIPLIERS] as number[]),
      MAGNIFY_SPRING,
    );

    const magnifyEnabled = (mousePos != null || rail.mouseAxisPos != null) && useMotion;

    // The motion.div owns the tile box. The box is FIXED at the base px size in
    // every context; in a dock (magnify on) we apply the scale spring as a
    // transform from the center. Reduced motion / standalone → scale clamps to
    // 1 (the fallback Infinity distance), so the tile is static.
    const tileBoxStyle = {
      width: tilePx,
      height: tilePx,
      ...(magnifyEnabled
        ? {
            scale,
            transformOrigin: "center" as const,
            willChange: "transform" as const,
          }
        : null),
    };

    const showInlineLabel = mode !== "tooltip";
    const collapsed = mode === "hover";

    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(railNavButtonVariants({ variant, size }), className)}
        {...props}
      >
        {/* GLASS TILE — the visual hero. Two-layer frosted surface (nx-m notch)
            with the icon centered. Box is FIXED at `tilePx`; in a dock the
            magnify spring drives `scale` (a transform — GPU-composited, zero
            reflow). Active variant draws its selection keyline as an `nx-rim`
            ring (the SAME nx-m silhouette) so it traces the notch + every corner
            continuously — a Tailwind `ring`/`border` on the nx-clipped node gets
            sliced at the notch diagonal. */}
        <motion.div style={tileBoxStyle} className="relative shrink-0">
          <GlassSurface
            ref={tileRef as React.Ref<HTMLElement>}
            variant="panel"
            nx="m"
            wrapperClassName="h-full w-full"
            className="flex h-full w-full items-center justify-center"
          >
            <span
              className="flex items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0"
              style={{ width: iconPx, height: iconPx }}
            >
              {icon}
            </span>
          </GlassSurface>
          {variant === "active" ? (
            // Selection keyline — a hollow 1px frame on the nx-m RING clip, so
            // it follows the notch + corners with no measurement and no clipping.
            // `--nx-rim` is the accent fill; the frame's interior is transparent.
            <span
              aria-hidden
              className="nx-rim pointer-events-none absolute inset-0 z-[3]"
              style={
                {
                  "--nx-rim": "hsl(var(--primary))",
                  clipPath: NX_RING_CLIP.m,
                } as React.CSSProperties
              }
            />
          ) : null}
        </motion.div>

        {showInlineLabel ? (
          <span
            className={cn(
              "min-w-0 overflow-hidden whitespace-nowrap",
              collapsed
                ? cn(
                    "max-w-0 opacity-0 transition-all duration-320 ease-smooth-out",
                    // Reveal on focus (keyboard a11y) and hover. NB synthetic
                    // hover does not drive `:hover` in headless browsers — focus
                    // is the testable path; see the stories' focus play-test.
                    "group-hover:max-w-[12rem] group-hover:opacity-100",
                    "group-focus-within:max-w-[12rem] group-focus-within:opacity-100",
                    "motion-reduce:transition-none",
                  )
                : "max-w-[12rem] opacity-100",
            )}
          >
            {label}
          </span>
        ) : (
          // tooltip mode: keep the name in the a11y tree, visually hidden.
          <span className="sr-only">{label}</span>
        )}
      </button>
    );
  },
);

RailNavButton.displayName = "RailNavButton";
